import { inject, Injectable } from '@angular/core';
import { BaseDatabaseService } from '@core/services/database/base-database.service';
import { environment } from '@env';
import { isPlatform } from '@ionic/angular';
import {
  prepareINSERT,
  prepareUPDATE,
  prepareVALUES
} from '@core/services/database/models/transaction.interface';
import { v4 as uuid } from 'uuid';
import {
  ClientDB,
  SearchClientParams,
  testClientsData
} from '@core/services/database/models/сlient.interface';
import { LoggerService } from '@core/services/logger/logger';
import { ClientOperationsService } from '@core/services/database/client-operations.service';

@Injectable({
  providedIn: 'root',
})
export class ClientsServiceDB extends BaseDatabaseService {
  private loggerService = inject(LoggerService);
  private clientOperationsService = inject(ClientOperationsService);

  /**
   * Delete all Clients and setup test data
   * @returns
   */
  async clearAndAddTestData(): Promise<boolean> {
    const res = await this.deleteClients();
    if (res) {
      for (const testClient of testClientsData) {
        await this.createClient(testClient, true);
      }
    }
    return true;
  }

  /**
   * Get all Clients
   * @returns
   */
  async getClients(
    searchParams: SearchClientParams,
    offset: number = 10,
    limit: number
  ): Promise<ClientDB[]> {
    let order = `created DESC`;
    let query = 'SELECT * FROM clients';
    const queryValues: any[] = [];
    if (searchParams.type && searchParams.search) {
      switch (searchParams.type) {
        case 'all':
          query += ` WHERE qr_code = ? OR rfid_tag = ?`;
          queryValues.push(searchParams.search, searchParams.search);
          break;
        case 'qr_code':
          query += ` WHERE qr_code = ?`;
          queryValues.push(searchParams.search);
          break;
        case 'rfid_tag':
          query += ` WHERE rfid_tag = ?`;
          queryValues.push(searchParams.search);
          break;
      }
    }
    query += ` ORDER BY ${order}`;
    query += ` LIMIT ${offset}, ${limit}`;

    const values = await this.querySafe(query, queryValues.length ? queryValues : undefined);
    this.loggerService.info('Clients', 'getClients', `$sql: ${query}`);
    return values.values as ClientDB[];

    // return (await this.querySafe(query)).values as Client[];
  }

  async getClientByCode(code: string): Promise<ClientDB | null> {
    const query = `SELECT * FROM clients WHERE qr_code = ?`;

    if (!environment.production) {
      console.log('#SQLClientByCode', query, [code]);
    }

    let ret: any = await this.querySafe(query, [code]);
    if (ret.values.length > 0) {
      return ret.values[0] as ClientDB;
    }
    return null;
  }

  /**
   * Get Client by QR code or by RFID tag.
   * A client is created with a QR code OR an RFID tag (one of them is enough),
   * so an authentication / search value has to be matched against both fields.
   * A match on the QR code wins if the same value is used by both fields.
   * @returns
   */
  async getClientByCodeOrRFID(value: string): Promise<ClientDB | null> {
    const query = `SELECT * FROM clients WHERE qr_code = ? OR rfid_tag = ?
      ORDER BY CASE WHEN qr_code = ? THEN 0 ELSE 1 END LIMIT 1`;

    if (!environment.production) {
      console.log('#SQLClientByCodeOrRFID', query, [value]);
    }

    let ret: any = await this.querySafe(query, [value, value, value]);
    if (ret.values.length > 0) {
      return ret.values[0] as ClientDB;
    }
    return null;
  }

  async getClientByRFID(rfid_tag: string): Promise<ClientDB | null> {
    const query = `SELECT * FROM clients WHERE rfid_tag = ?`;

    if (!environment.production) {
      console.log('#SQLClientByRFID', query, [rfid_tag]);
    }

    let ret: any = await this.querySafe(query, [rfid_tag]);
    if (ret.values.length > 0) {
      return ret.values[0] as ClientDB;
    }
    return null;
  }

  /**
   * Create Client
   * @returns
   */
  async createClient(
    client: ClientDB,
    logOperation?: boolean
  ): Promise<boolean> {
    if (!client.uuid) {
      client.uuid = uuid();
    }

    if (logOperation) {
      const timestamp = new Date().getTime();
      client.created = timestamp;
      client.updated = timestamp;
    }

    const { sql, values } = prepareINSERT('clients', client);

    if (!environment.production) {
      console.log('#SQLCreateClient', sql);
    }

    try {
      await this.querySafe(sql, prepareVALUES(values));
      if (!isPlatform('hybrid')) {
        await this.sqliteService.sqliteConnection.saveToStore(
          this.databaseName
        );
      }

      this.loggerService.info(
        'Clients',
        'createClient',
        `$sql: ${sql} ${JSON.stringify(values)}`
      );

      if (!logOperation) {
        return true;
      }

      const createdClient = await this.getClientByUUID(client.uuid);

      if (createdClient) {
        await this.clientOperationsService.addOperation(
          createdClient,
          'create'
        );
      }

      return true;
    } catch (e: any) {
      this.loggerService.error('Clients', 'createClient', e);
      return false;
    }
  }

  async updateClient(
    uuid: ClientDB['uuid'],
    client: Partial<ClientDB>,
    logOperation?: boolean
  ): Promise<boolean> {
    if (!uuid) {
      return false;
    }
    client.updated = new Date().getTime();
    const { sql, values } = prepareUPDATE('clients', client);
    try {
      await this.querySafe(`${sql} WHERE uuid=?`, [
        ...prepareVALUES(values),
        uuid
      ]);
      if (!isPlatform('hybrid')) {
        await this.sqliteService.sqliteConnection.saveToStore(
          this.databaseName
        );
      }

      this.loggerService.info(
        'Clients',
        'updateClient',
        `$sql: ${sql} WHERE id=? ${JSON.stringify(values)}`
      );

      if (!logOperation) {
        return true;
      }

      const updatedClient = await this.getClientByUUID(uuid);

      if (updatedClient) {
        await this.clientOperationsService.addOperation(
          updatedClient,
          'update'
        );
      }

      return true;
    } catch (e) {
      this.loggerService.error('Clients', 'updateClient', e);
      return false;
    }
  }

  /**
   * Get count Clients
   * @returns
   */
  async countClients() {
    let sqlcmd: string = 'SELECT COUNT(*) as count FROM clients';
    let ret: any = await this.querySafe(sqlcmd);
    if (ret.values.length > 0) {
      return ret.values[0].count;
    }
    throw Error('count clients failed');
  }

  /**
   * Get Client by uuid
   * @returns
   */
  async getClientByUUID(uuid: string): Promise<ClientDB | null> {
    let sqlcmd: string = 'SELECT * FROM clients WHERE uuid = ? LIMIT 1';
    let values: Array<any> = [uuid];
    let ret: any = await this.querySafe(sqlcmd, values);

    if (ret.values.length > 0) {
      return ret.values[0] as ClientDB;
    }
    return null;
  }

  /**
   * Delete Client by uuid
   * @returns
   */
  async deleteClientByUUID(
    uuid: string,
    logOperation?: boolean
  ): Promise<boolean> {
    try {
      const client = await this.getClientByUUID(uuid);

      if (!client) {
        return false;
      }

      client.updated = new Date().getTime();

      await this.querySafe(`DELETE FROM clients WHERE uuid = ?`, [uuid]);

      if (!isPlatform('hybrid')) {
        await this.sqliteService.sqliteConnection.saveToStore(
          this.databaseName
        );
      }

      if (!logOperation) {
        return true;
      }

      await this.clientOperationsService.addOperation(client, 'delete');

      return true;
    } catch (e) {
      this.loggerService.error('Clients', 'deleteClient', e);
      return false;
    }
  }

  /**
   * Delete all Clients
   * @returns
   */
  async deleteClients(): Promise<boolean> {
    await this.querySafe(`DELETE FROM clients`);
    if (!isPlatform('hybrid')) {
      await this.sqliteService.sqliteConnection.saveToStore(this.databaseName);
    }
    return true;
  }
}
